import { FC } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Avatar from '@mui/material/Avatar';
import MenuItem from '@mui/material/MenuItem';
import { Divider, ListItemIcon } from '@mui/material';
import { AssignmentOutlined, Group, Logout } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { getUserInfo } from 'redux/reducers/user/selectors';
import { stringAvatar } from 'utils/stringAvatar';
import { getUserImage } from 'utils/getUserImage';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import GrayscaleIcon from '@mui/icons-material/Grain';
import CheckIcon from '@mui/icons-material/Check';
import { COLORS, COLORS_THEMES, EThemeName, getTheme, setTheme } from 'services/constants/colors';
import { useNavigate } from 'react-router-dom';
import { TUserMenuProps } from './types';
import { PATHS, ROLES } from '../../../routes/constants';
import { SvgIconComponent } from '@mui/icons-material';
import { IS_NEMAN } from '../../../services/constants/env';
import { ConnectedEmerchants } from './ConnectedEmerchants';

const THEME_OPTIONS: { theme: EThemeName; label: string; Icon: SvgIconComponent }[] = [
	{ theme: EThemeName.LIGHT, label: 'EAS Light', Icon: LightModeIcon },
	{ theme: EThemeName.DARK, label: 'EAS Dark', Icon: DarkModeIcon },
	{ theme: EThemeName.LIGHT_GRAY, label: 'Light Gray', Icon: GrayscaleIcon },
	{ theme: EThemeName.DARK_GREY, label: 'Dark Grey', Icon: DarkModeIcon },
	{ theme: EThemeName.VAT_LIGHT, label: 'Aquamarine', Icon: LightModeIcon },
	{ theme: EThemeName.VAT_DARK, label: 'Aquamarine Dark', Icon: DarkModeIcon },
	{ theme: EThemeName.NEMAN_LIGHT, label: 'Neman Light', Icon: LightModeIcon },
	{ theme: EThemeName.NEMAN_DARK, label: 'Neman Dark', Icon: DarkModeIcon },
];

export const UserMenu: FC<TUserMenuProps> = ({
	anchorElUser,
	handleCloseUserMenu,
	handleLogout,
}) => {
	const navigate = useNavigate();
	const userinfo = useSelector(getUserInfo);
	return (
		<Menu
			sx={{ mt: '45px' }}
			id="menu-appbar"
			anchorEl={anchorElUser}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			keepMounted
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			open={Boolean(anchorElUser)}
			onClose={handleCloseUserMenu}
		>
			<Box
				sx={{
					mt: '-8px',
					p: '14px 16px 14px 16px',
					minWidth: 280,
					bgcolor: COLORS.BACKGROUND_SECONDARY,
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'start' }}>
					<Avatar
						src={getUserImage(userinfo?.email || '')}
						{...stringAvatar(userinfo?.fullName || '')}
					/>
					<Box sx={{ pl: '10px' }}>
						<Typography textAlign="left" variant="h6" component="div">
							{userinfo?.fullName ?? ''}
						</Typography>
						<Typography textAlign="left" color="text.secondary" variant="body2">
							{userinfo?.email ?? ''}
						</Typography>
						{/* <Typography
							textAlign="left"
							variant="body1"
							// color="white"
						>
							{organization?.group_name ?? ''}
						</Typography> */}
						<Typography
							textAlign="left"
							variant="body1"
							// color="white"
						>
							{userinfo?.roles?.find((role) => role.roleName === 'admin') ? 'Administrator' : ''}
						</Typography>
					</Box>
				</Box>{' '}
			</Box>
			{/* <MenuItem disabled sx={{ opacity: '1!important' }}>
      <ListItemIcon>
        <BusinessCenterIcon fontSize="small" />
      </ListItemIcon>
      <Typography textAlign="left" variant="body1">
        {organization?.group_name ?? ''}
      </Typography>
    </MenuItem>
    <Divider variant="middle" /> */}

			{/* <MenuItem onClick={handleLogout} sx={{ pt: '14px' }}>
				<ListItemIcon>
					<HistoryEduIcon fontSize="small" />
				</ListItemIcon>
				<Typography textAlign="center">Contract</Typography>
			</MenuItem>
			<Divider variant="middle" />
			<MenuItem onClick={handleLogout}>
				<ListItemIcon>
					<Settings fontSize="small" />
				</ListItemIcon>
				<Typography textAlign="center">General Settings</Typography>
			</MenuItem>
			<MenuItem onClick={handleLogout}>
				<ListItemIcon>
					<NotificationsNoneIcon fontSize="small" />
				</ListItemIcon>

				<Typography textAlign="center">Notification Settings</Typography>
			</MenuItem>
			<MenuItem onClick={handleLogout}>
				<ListItemIcon>
					<SecurityIcon fontSize="small" />
				</ListItemIcon>

				<Typography textAlign="center">Security Settings</Typography>
			</MenuItem> */}
			{userinfo?.roles?.find((role) => role.roleNameUI === ROLES.ADMIN) ? (
				<MenuItem onClick={() => navigate(PATHS.USER_MANAGEMENT_PAGE)} sx={{ pt: '14px' }}>
					<ListItemIcon sx={{ color: COLORS.PRIMARY }}>
						<Group fontSize="small" />
					</ListItemIcon>
					<Typography
						textAlign="left"
						variant="body1"
						sx={{
							color: COLORS.PRIMARY,
						}}
					>
						User Management
					</Typography>
				</MenuItem>
			) : null}
			<MenuItem onClick={() => navigate(PATHS.TASK_TRACKER_PAGE)} sx={{ pt: '14px' }}>
				<ListItemIcon sx={{ color: COLORS.PRIMARY }}>
					<AssignmentOutlined fontSize="small" />
				</ListItemIcon>
				<Typography textAlign="left" variant="body1" sx={{ color: COLORS.PRIMARY }}>
					Task Tracker
				</Typography>
			</MenuItem>
			<ConnectedEmerchants />
			{!IS_NEMAN && <Divider variant="middle" />}
			{!IS_NEMAN && (
				<MenuItem disabled sx={{ opacity: '1!important', pb: 0 }}>
					<Typography textAlign="left" variant="body2" color="text.secondary">
						Themes
					</Typography>
				</MenuItem>
			)}
			{!IS_NEMAN && (
				<Box sx={{ mt: 1, p: 1 }}>
					{THEME_OPTIONS.map(({ theme, label, Icon }) => {
						const colors = COLORS_THEMES[theme];
						const isActive = getTheme() === theme;
						return (
							<Box
								key={theme}
								sx={{
									backgroundColor: colors.PAPER,
									borderRadius: '12px',
									mb: 1,
									border: isActive ? `2px solid ${colors.PRIMARY}` : '2px solid transparent',
								}}
							>
								<MenuItem
									onClick={() => {
										setTheme(theme);
										handleCloseUserMenu();
										window.location.reload();
									}}
									sx={{
										color: colors.PRIMARY,
										backgroundColor: colors.BACKGROUND,
										borderRadius: '10px',
										'&:hover': {
											backgroundColor: colors.BACKGROUND_SECONDARY,
										},
									}}
								>
									<ListItemIcon>
										<Icon fontSize="small" sx={{ color: colors.PRIMARY }} />
									</ListItemIcon>
									<Typography textAlign="left" sx={{ flexGrow: 1, color: colors.font_main }}>
										{label}
									</Typography>
									{isActive ? <CheckIcon fontSize="small" sx={{ color: colors.PRIMARY }} /> : null}
								</MenuItem>
							</Box>
						);
					})}
				</Box>
			)}
			<Divider variant="middle" />
			<MenuItem onClick={handleLogout}>
				<ListItemIcon>
					<Logout fontSize="small" />
				</ListItemIcon>
				<Typography textAlign="left">Logout</Typography>
			</MenuItem>
		</Menu>
	);
};
